import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Phone, ShieldCheck } from 'lucide-react';
import { getInitials } from '../lib/utils';

const COUNTRY_CODES = [
  { code: '+91', country: 'IN', flag: '🇮🇳', label: 'India' },
  { code: '+1', country: 'US', flag: '🇺🇸', label: 'USA' },
  { code: '+44', country: 'GB', flag: '🇬🇧', label: 'UK' },
  { code: '+971', country: 'AE', flag: '🇦🇪', label: 'UAE' },
  { code: '+65', country: 'SG', flag: '🇸🇬', label: 'Singapore' },
  { code: '+61', country: 'AU', flag: '🇦🇺', label: 'Australia' },
  { code: '+49', country: 'DE', flag: '🇩🇪', label: 'Germany' },
  { code: '+33', country: 'FR', flag: '🇫🇷', label: 'France' },
  { code: '+81', country: 'JP', flag: '🇯🇵', label: 'Japan' },
  { code: '+86', country: 'CN', flag: '🇨🇳', label: 'China' },
  { code: '+82', country: 'KR', flag: '🇰🇷', label: 'South Korea' },
  { code: '+60', country: 'MY', flag: '🇲🇾', label: 'Malaysia' },
  { code: '+62', country: 'ID', flag: '🇮🇩', label: 'Indonesia' },
  { code: '+66', country: 'TH', flag: '🇹🇭', label: 'Thailand' },
  { code: '+27', country: 'ZA', flag: '🇿🇦', label: 'South Africa' },
  { code: '+234', country: 'NG', flag: '🇳🇬', label: 'Nigeria' },
  { code: '+254', country: 'KE', flag: '🇰🇪', label: 'Kenya' },
  { code: '+55', country: 'BR', flag: '🇧🇷', label: 'Brazil' },
  { code: '+52', country: 'MX', flag: '🇲🇽', label: 'Mexico' },
];

export default function GoogleRegister() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();

  const email = searchParams.get('email') || '';
  const googleName = searchParams.get('name') || '';
  const picture = searchParams.get('picture') || '';
  const googleId = searchParams.get('google_id') || '';

  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  const routeByRole = (role) => {
    if (role === 'superadmin' || role === 'admin') return '/admin/dashboard';
    if (role === 'instructor') return '/instructor/dashboard';
    if (role === 'doctor') return '/doctor/dashboard';
    return '/learner/dashboard';
  };

  const handleSendOtp = async () => {
    if (!phone || phone.length < 6) { toast.error('Please enter a valid phone number'); return; }
    setSendingOtp(true);
    try {
      const fullPhone = `${countryCode}${phone}`;
      await api.post('/api/auth/send-otp', { phone: fullPhone });
      setOtpSent(true);
      toast.success(`OTP sent via WhatsApp to ${countryCode} ${phone}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    }
    setSendingOtp(false);
  };

  const handleVerifyOtp = async () => {
    try {
      const fullPhone = `${countryCode}${phone}`;
      await api.post('/api/auth/verify-otp', { phone: fullPhone, otp });
      setOtpVerified(true);
      toast.success('Phone number verified!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    }
  };

  const handleRegister = async () => {
    if (!otpVerified) { toast.error('Please verify your phone number first'); return; }

    setLoading(true);
    try {
      const referredByCode = localStorage.getItem('ss_referred_by_code') || null;
      const res = await api.post('/api/auth/google/register', {
        email,
        name: googleName,
        phone: `${countryCode}${phone}`,
        google_id: googleId,
        picture,
        referred_by_code: referredByCode,
      });

      if (referredByCode) localStorage.removeItem('ss_referred_by_code');

      // V14.5 Part 17 — BLACK SCREEN POST-OTP FIX.
      // Pre-fix: localStorage.setItem(token) → await refreshUser() (a second
      // GET /api/auth/me round-trip) → navigate('/learner/dashboard').
      // The second fetch could race with the just-committed write (replica
      // lag on Mongo / token propagation), silently 401, clear localStorage,
      // and set user=null. The dashboard route then tried to render with
      // a null user → first hook in LearnerDashboard.js destructured
      // `user.learner_type` → TypeError → ErrorBoundary OR worse, a brief
      // unmounted-state blank (black on mobile dark-mode preference).
      // Fix: use setAuth({token, user}) directly with the user object the
      // register endpoint returned (no extra fetch, no race). Wrap navigate
      // in a try/catch so a routing failure can't strand the user. Also
      // best-effort send the browser's timezone for booking-msg formatting.
      const u = res.data?.user;
      const tok = res.data?.access_token;
      if (!u || !tok) {
        throw new Error('Server did not return a valid session.');
      }
      setAuth({ token: tok, user: u });

      // Best-effort capture browser timezone (V14.5 Part 17 — for WATI msg TZ)
      try {
        const tz = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone;
        if (tz) api.put('/api/users/me/timezone', { timezone: tz }).catch(() => { });
      } catch { /* noop */ }

      toast.success(`Welcome to Soul Sanskar, ${(googleName || u.name || '').split(' ')[0]}!`);
      // Wait one tick so React commits the auth state before navigation.
      setTimeout(() => {
        try {
          navigate(routeByRole(u.role), { replace: true });
        } catch (e) {
          // Last-resort: hard redirect if navigate misfires.
          window.location.href = routeByRole(u.role);
        }
      }, 50);
    } catch (err) {
      console.error('[GoogleRegister] failed', err);
      // V14.5 Part 17k — `detail` is now always a string (api interceptor).
      // Structured codes live on `detailObject`.
      const data = err?.response?.data || {};
      const code = data.detailObject?.code || null;
      const msg = data.detail || err?.message || 'Registration failed. Please try again.';
      toast.error(msg);
      // V14.5 Part 17f — PHONE_EXISTS means another account already owns this
      // phone. Send the user back to /login so they sign in with the email
      // tied to that phone (or use a different phone).
      if (code === 'PHONE_EXISTS') {
        setTimeout(() => navigate('/login'), 2000);
      }
      setLoading(false);
      return;
    }
    // Note: don't reset loading on success — page is unmounting.
  };

  if (!email) { navigate('/login'); return null; }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 h-24 w-24 rounded-2xl overflow-hidden bg-white flex items-center justify-center shadow-sm border">
            <img src="/logo.png" alt="Soul Sanskar" className="h-32 w-32 object-contain scale-150" />
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-foreground">Welcome!</h1>
          <p className="text-muted-foreground mt-1">Verify your phone number to get started</p>
        </div>

        <Card className="rounded-2xl border bg-white shadow-sm">
          <CardContent className="pt-6 pb-6">
            {/* Google Profile */}
            <div className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-muted/50">
              <Avatar className="h-10 w-10">
                {picture ? <AvatarImage src={picture} /> : null}
                <AvatarFallback className="bg-primary/10 text-primary">{getInitials(googleName)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">{googleName}</p>
                <p className="text-xs text-muted-foreground">{email}</p>
              </div>
            </div>

            {/* Phone with Country Code */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Phone Number</label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode} disabled={otpSent}>
                    <SelectTrigger className="w-[110px] shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4} className="z-[200] max-h-48">
                      {COUNTRY_CODES.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className="flex items-center gap-1.5">{c.flag} {c.code}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                    placeholder="98765 43210"
                    className="flex-1"
                    /* V13.14.6 — once OTP is in flight or already verified, lock
                       the field. Lets the user use "Change number?" below to
                       reset deliberately instead of silently editing it. */
                    disabled={otpSent || otpVerified}
                    data-testid="register-phone"
                  />
                </div>
              </div>

              {/* OTP Section */}
              {!otpSent ? (
                <Button className="w-full" variant="outline" onClick={handleSendOtp} disabled={sendingOtp || !phone}>
                  {sendingOtp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
                  Send OTP
                </Button>
              ) : !otpVerified ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Enter OTP</label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={otp}
                        onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit OTP"
                        className="flex-1 text-center tracking-[0.3em] font-mono text-lg"
                        maxLength={6}
                        data-testid="register-otp"
                      />
                      <Button onClick={handleVerifyOtp} disabled={otp.length !== 6}>Verify</Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">OTP sent to {countryCode} {phone}</p>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="text-xs h-7"
                              onClick={() => { setOtpSent(false); setOtp(''); }}
                              data-testid="register-change-number">
                        Change number
                      </Button>
                      <Button variant="ghost" size="sm" className="text-xs h-7" onClick={handleSendOtp} disabled={sendingOtp}>Resend</Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-900">Phone Verified</p>
                    <p className="text-xs text-emerald-700">{countryCode} {phone}</p>
                  </div>
                </div>
              )}

              {/* Create Account Button */}
              <Button className="w-full" onClick={handleRegister} disabled={loading || !otpVerified} data-testid="register-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Account & Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
